import { useRef } from 'react';
import { WidgetContainer } from '@/components/analytics/WidgetContainer';
import { LineChart } from '@/components/analytics/charts/LineChart';
import { useChartDimensions } from '@/hooks/useChartDimensions';
import type { ArticleAnalyticsData } from '@/types/analytics';
import styles from './ArticleViewsChart.module.scss';

export interface ArticleViewsChartProps {
  data: ArticleAnalyticsData | null;
  isLoading?: boolean;
  error?: Error | null;
}

/**
 * Article Views Chart Widget
 *
 * Displays a line chart showing article views over time.
 */
export function ArticleViewsChart({ data, isLoading, error }: ArticleViewsChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { width, height } = useChartDimensions(containerRef as any);

  const isEmpty = !data || !data.viewsTimeSeries || data.viewsTimeSeries.length === 0;

  const chartSeries = data?.viewsTimeSeries
    ? [
        {
          name: 'Views',
          data: data.viewsTimeSeries.map((d) => ({ x: d.date, y: d.views })),
          color: 'var(--primary)',
        },
      ]
    : [];

  return (
    <WidgetContainer
      title="Article Views"
      description="Views over time"
      isLoading={isLoading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage="Views will appear here as your articles are read"
      size="medium"
    >
      <div ref={containerRef} className={styles.articleViewsChart}>
        {width > 0 && height > 0 && (
          <LineChart
            series={chartSeries}
            width={width}
            height={height}
            showDots
            showGrid
            animate
          />
        )}
      </div>
    </WidgetContainer>
  );
}
